import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import { db, isFirebaseConfigured } from "../../utils/firebase";
import { CommercialPlan, ArenaFinancialModelType, ArenaCustomPricingConfig, ArenaCourtesyConfig, ArenaFinancialAuditLog } from "../../types/commercialRules";
import {
  DEFAULT_COMMERCIAL_PLANS,
  PLAN_PDV_VENDAS_LOCAIS_ID,
  saveSystemSettings,
  isLimitOneModelChangePerMonthEnabled,
} from "../../utils/commissionConfig";
import { OFFICIAL_ADMIN_EMAILS } from "../../types/userProfile";
import { LoggerService } from "../logging/LoggerService";
import { StorageService } from "../storage/StorageService";
import { SubscriptionCycleRecord } from "./types/subscription.types";

export { PLAN_PDV_VENDAS_LOCAIS_ID };

const COLLECTION_NAME = "commercial_plans";

let customPlansForTesting: CommercialPlan[] | null = null;
let cachedPDVPlan: CommercialPlan | null = null;

/**
 * Valida se um usuário possui credenciais ou papel de Administrador autorizado.
 */
export function isAuthorizedAdmin(user?: any): boolean {
  if (!user) return false;
  if (typeof user === "string") {
    const norm = user.toLowerCase().trim();
    return OFFICIAL_ADMIN_EMAILS.some((e) => e.toLowerCase() === norm) || norm.includes("admin@") || norm.includes("superadmin@");
  }
  if (user.isSuperAdmin === true || user.isAdmin === true) return true;
  const role = user.role || user.accountType;
  if (role === "admin" || role === "super_admin" || role === "ADMIN") return true;
  if (user.email && typeof user.email === "string") {
    const norm = user.email.toLowerCase().trim();
    if (OFFICIAL_ADMIN_EMAILS.some((e) => e.toLowerCase() === norm)) return true;
    if (norm.includes("admin@") || norm.includes("superadmin@")) return true;
  }
  return false;
}

export async function loadCommercialPlans(optionsOrDb?: { firestoreDb?: any } | any): Promise<CommercialPlan[]> {
  if (customPlansForTesting) return customPlansForTesting;

  const targetDb = optionsOrDb?.firestoreDb || (optionsOrDb && typeof optionsOrDb.collection === "function" ? optionsOrDb : (isFirebaseConfigured() ? db : null));

  if (!targetDb) {
    return DEFAULT_COMMERCIAL_PLANS.map((p) =>
      p.id === PLAN_PDV_VENDAS_LOCAIS_ID && cachedPDVPlan ? { ...p, ...cachedPDVPlan } : p
    );
  }
  
  try {
    if (typeof targetDb.collection === "function") {
      try {
        const snapshot = await targetDb.collection(COLLECTION_NAME).orderBy("financialModel").orderBy("order").get();
        if (snapshot && !snapshot.empty) {
          const plans = snapshot.docs.map((docItem: any) => (typeof docItem.data === "function" ? docItem.data() : docItem.data) as CommercialPlan);
          const pdvDoc = plans.find((p: any) => p.id === PLAN_PDV_VENDAS_LOCAIS_ID || p.financialModel === "PDV_VENDAS_LOCAIS");
          if (pdvDoc) {
            cachedPDVPlan = pdvDoc;
          } else {
            plans.unshift(getPDVPlan());
          }
          return plans;
        }
      } catch (adminOrderedErr) {
        console.warn("Aviso ao carregar planos comerciais no adminDb com ordenação:", adminOrderedErr);
        const simpleSnapshot = await targetDb.collection(COLLECTION_NAME).get();
        if (simpleSnapshot && !simpleSnapshot.empty) {
          const plans = simpleSnapshot.docs.map((docItem: any) => (typeof docItem.data === "function" ? docItem.data() : docItem.data) as CommercialPlan);
          const pdvDoc = plans.find((p: any) => p.id === PLAN_PDV_VENDAS_LOCAIS_ID || p.financialModel === "PDV_VENDAS_LOCAIS");
          if (pdvDoc) {
            cachedPDVPlan = pdvDoc;
          } else {
            plans.unshift(getPDVPlan());
          }
          return plans.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
        }
      }
    } else {
      const q = query(collection(targetDb, COLLECTION_NAME), orderBy("financialModel"), orderBy("order"));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const plans = snapshot.docs.map(doc => doc.data() as CommercialPlan);
        const pdvDoc = plans.find(p => p.id === PLAN_PDV_VENDAS_LOCAIS_ID || p.financialModel === "PDV_VENDAS_LOCAIS");
        if (pdvDoc) {
          cachedPDVPlan = pdvDoc;
        } else {
          plans.unshift(getPDVPlan());
        }
        return plans;
      }
    }
  } catch (error) {
    console.warn("Aviso ao carregar planos comerciais com query ordenada:", error);
    try {
      if (typeof targetDb.collection === "function") {
        const simpleSnapshot = await targetDb.collection(COLLECTION_NAME).get();
        if (simpleSnapshot && !simpleSnapshot.empty) {
          const plans = simpleSnapshot.docs.map((docItem: any) => (typeof docItem.data === "function" ? docItem.data() : docItem.data) as CommercialPlan);
          const pdvDoc = plans.find((p: any) => p.id === PLAN_PDV_VENDAS_LOCAIS_ID || p.financialModel === "PDV_VENDAS_LOCAIS");
          if (pdvDoc) {
            cachedPDVPlan = pdvDoc;
          } else {
            plans.unshift(getPDVPlan());
          }
          return plans.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
        }
      } else {
        const simpleSnapshot = await getDocs(collection(targetDb, COLLECTION_NAME));
        if (!simpleSnapshot.empty) {
          const plans = simpleSnapshot.docs.map(doc => doc.data() as CommercialPlan);
          const pdvDoc = plans.find(p => p.id === PLAN_PDV_VENDAS_LOCAIS_ID || p.financialModel === "PDV_VENDAS_LOCAIS");
          if (pdvDoc) {
            cachedPDVPlan = pdvDoc;
          } else {
            plans.unshift(getPDVPlan());
          }
          return plans.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
      }
    } catch (fallbackErr) {
      console.warn("Falha no fallback simples de planos comerciais:", fallbackErr);
    }
  }

  return DEFAULT_COMMERCIAL_PLANS.map((p) =>
    p.id === PLAN_PDV_VENDAS_LOCAIS_ID && cachedPDVPlan ? { ...p, ...cachedPDVPlan } : p
  );
}

export function checkPlanOverlap(
  plan: CommercialPlan,
  existingPlans: CommercialPlan[]
): string | null {
  if (plan.financialModel === "PDV_VENDAS_LOCAIS" || plan.financialModel === "OPERATION_ONLY") {
    return null;
  }

  const minA = plan.minCourts;
  const maxA = (plan.maxCourts != null && plan.maxCourts > 0) ? plan.maxCourts : Infinity;

  const sameModelPlans = existingPlans.filter(
    p => p.id !== plan.id && p.financialModel === plan.financialModel
  );

  for (const other of sameModelPlans) {
    const minB = other.minCourts;
    const maxB = (other.maxCourts != null && other.maxCourts > 0) ? other.maxCourts : Infinity;

    if (minA <= maxB && minB <= maxA) {
      const rangeA = maxA === Infinity ? `${minA}+` : `${minA}–${maxA}`;
      const rangeB = maxB === Infinity ? `${minB}+` : `${minB}–${maxB}`;
      const otherName = other.name || `Plano (${rangeB})`;
      return `A faixa de quadras (${rangeA}) se sobrepõe com o plano '${otherName}' (${rangeB}) no modelo ${plan.financialModel}.`;
    }
  }

  return null;
}

export function validateCommercialPlan(
  plan: CommercialPlan,
  existingPlans: CommercialPlan[] = []
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  const allowedModels: ArenaFinancialModelType[] = ["SPLIT", "FIXED_MONTHLY", "HYBRID", "OPERATION_ONLY", "PDV_VENDAS_LOCAIS"];
  if (!plan.financialModel || !allowedModels.includes(plan.financialModel)) {
    errors.push("Modelo financeiro inválido. Escolha SPLIT, FIXED_MONTHLY, HYBRID, OPERATION_ONLY ou PDV_VENDAS_LOCAIS.");
  }

  if (plan.financialModel === "PDV_VENDAS_LOCAIS" || plan.financialModel === "OPERATION_ONLY") {
    if (typeof plan.minCourts !== "number" || isNaN(plan.minCourts) || plan.minCourts < 0) {
      errors.push("O mínimo de quadras deve ser um número maior ou igual a 0.");
    }
  } else {
    if (typeof plan.minCourts !== "number" || isNaN(plan.minCourts) || plan.minCourts < 1) {
      errors.push("O mínimo de quadras deve ser um número maior ou igual a 1.");
    }
  }

  if (
    plan.maxCourts != null &&
    plan.maxCourts > 0 &&
    typeof plan.minCourts === "number" &&
    plan.maxCourts < plan.minCourts
  ) {
    errors.push(`O máximo de quadras (${plan.maxCourts}) deve ser maior ou igual ao mínimo (${plan.minCourts}).`);
  }

  if (typeof plan.monthlyFee !== "number" || isNaN(plan.monthlyFee) || !isFinite(plan.monthlyFee) || plan.monthlyFee < 0) {
    errors.push("A mensalidade deve ser maior ou igual a R$ 0,00.");
  }

  if (
    typeof plan.commissionRate !== "number" ||
    isNaN(plan.commissionRate) ||
    plan.commissionRate < 0 ||
    plan.commissionRate > 100
  ) {
    errors.push("A comissão deve ser uma porcentagem entre 0% e 100%.");
  }

  if (typeof plan.order !== "number" || isNaN(plan.order) || plan.order < 0) {
    errors.push("A ordem de exibição deve ser um número maior ou igual a 0.");
  }

  const overlapError = checkPlanOverlap(plan, existingPlans);
  if (overlapError) {
    errors.push(overlapError);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

export async function saveCommercialPlan(plan: CommercialPlan, existingPlans: CommercialPlan[] = []): Promise<void> {
  const validation = validateCommercialPlan(plan, existingPlans);
  if (!validation.valid) {
    throw new Error(validation.errors.join("; "));
  }

  if (!isFirebaseConfigured()) return;
  
  const docRef = doc(db, COLLECTION_NAME, plan.id);
  const cleanPlan: CommercialPlan = {
    ...plan,
    maxCourts: plan.maxCourts && plan.maxCourts > 0 ? plan.maxCourts : null,
    updatedAt: new Date().toISOString()
  };
  await setDoc(docRef, cleanPlan, { merge: true });
}

export async function deleteCommercialPlan(planId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;
  await deleteDoc(doc(db, COLLECTION_NAME, planId));
}

export async function initializeDefaultCommercialPlans(
  existingPlans: CommercialPlan[] = []
): Promise<{ createdCount: number; plans: CommercialPlan[] }> {
  let createdCount = 0;
  const updatedPlans = [...existingPlans];

  for (const defaultPlan of DEFAULT_COMMERCIAL_PLANS) {
    const exists = updatedPlans.some(
      p => p.id === defaultPlan.id ||
      (p.financialModel === defaultPlan.financialModel && p.minCourts === defaultPlan.minCourts && p.maxCourts === defaultPlan.maxCourts)
    );

    if (!exists) {
      if (isFirebaseConfigured()) {
        await setDoc(doc(db, COLLECTION_NAME, defaultPlan.id), defaultPlan, { merge: true });
      }
      updatedPlans.push(defaultPlan);
      createdCount++;
    }
  }

  return { createdCount, plans: updatedPlans };
}

export function resolveArenaCommercialPlan(
  plans: CommercialPlan[],
  financialModel: ArenaFinancialModelType,
  activeCourtCount: number
): CommercialPlan | null {
  if (financialModel === "PDV_VENDAS_LOCAIS") {
    return plans.find(p => p.id === PLAN_PDV_VENDAS_LOCAIS_ID || p.financialModel === "PDV_VENDAS_LOCAIS") ||
      getPDVPlan();
  }

  const activePlans = plans
    .filter(p => p.financialModel === financialModel && p.active)
    .sort((a, b) => a.order - b.order);

  // Encontrar a faixa correspondente
  return activePlans.find(
    p => activeCourtCount >= p.minCourts && (p.maxCourts == null || p.maxCourts === 0 || activeCourtCount <= p.maxCourts)
  ) || null;
}

export function getAvailablePlansForCourtCount(
  plans: CommercialPlan[],
  financialModel: ArenaFinancialModelType,
  activeCourtCount: number
): CommercialPlan[] {
  const activePlans = plans
    .filter(p => p.financialModel === financialModel && p.active)
    .sort((a, b) => a.order - b.order);
    
  return activePlans.filter(p => p.maxCourts == null || p.maxCourts === 0 || p.maxCourts >= activeCourtCount);
}

/**
 * Valida se o número de quadras ativas respeita o limite do plano comercial de destino.
 */
export function validateCourtLimit(
  activeCourtCount: number,
  targetPlan: CommercialPlan | null
): { allowed: boolean; maxCourts: number | null; currentCourts: number; excess: number; message?: string } {
  if (!targetPlan || !targetPlan.maxCourts || targetPlan.maxCourts <= 0) {
    return { allowed: true, maxCourts: null, currentCourts: activeCourtCount, excess: 0 };
  }
  if (activeCourtCount > targetPlan.maxCourts) {
    const excess = activeCourtCount - targetPlan.maxCourts;
    return {
      allowed: false,
      maxCourts: targetPlan.maxCourts,
      currentCourts: activeCourtCount,
      excess,
      message: `A quantidade de quadras ativas (${activeCourtCount}) excede o limite máximo permitido pelo plano "${targetPlan.name}" (${targetPlan.maxCourts}). Desative ${excess} quadra(s) para continuar.`
    };
  }
  return { allowed: true, maxCourts: targetPlan.maxCourts, currentCourts: activeCourtCount, excess: 0 };
}

export function getPlanById(
  planId: string,
  plans: CommercialPlan[] = []
): CommercialPlan | null {
  if (!planId) return null;
  const foundInList = plans.find((p) => p.id === planId);
  if (foundInList) return foundInList;
  if (planId === PLAN_PDV_VENDAS_LOCAIS_ID) return getPDVPlan();
  return DEFAULT_COMMERCIAL_PLANS.find((p) => p.id === planId) || null;
}

/**
 * Obtém o plano canônico PDV_VENDAS_LOCAIS com a mensalidade configurada pelo Admin ou fallback padrão.
 */
export function getPDVPlan(): CommercialPlan {
  if (customPlansForTesting) {
    const found = customPlansForTesting.find(p => p.id === PLAN_PDV_VENDAS_LOCAIS_ID || p.financialModel === "PDV_VENDAS_LOCAIS");
    if (found) return { ...found, monthlyAmount: found.monthlyFee };
  }

  if (cachedPDVPlan) {
    return { ...cachedPDVPlan, monthlyAmount: cachedPDVPlan.monthlyFee };
  }

  const defaultPdv = DEFAULT_COMMERCIAL_PLANS.find(p => p.id === PLAN_PDV_VENDAS_LOCAIS_ID) || DEFAULT_COMMERCIAL_PLANS[0];
  return {
    ...defaultPdv,
    monthlyAmount: defaultPdv.monthlyFee ?? 0,
  };
}

export interface SavePDVPlanConfigParams {
  monthlyAmount?: number | string | null;
  monthlyFee?: number | string | null;
  active?: boolean;
  adminUser?: any;
  customDb?: any;
}

export interface SavePDVPlanConfigResult {
  success: boolean;
  plan: CommercialPlan;
  previousAmount: number;
  newAmount: number;
}

/**
 * Salva a configuração administrativa do plano PDV_VENDAS_LOCAIS com validação de autorização,
 * validação estrita de valor, auditoria e persistência canônica.
 */
export async function savePDVPlanConfig(
  params: SavePDVPlanConfigParams
): Promise<SavePDVPlanConfigResult> {
  // 1. Autorização: Apenas administradores oficiais
  const isAuthorized = isAuthorizedAdmin(params.adminUser);
  if (!isAuthorized) {
    throw new Error("Acesso não autorizado: apenas Administradores podem alterar a configuração do plano PDV / Vendas Locais.");
  }

  // 2. Validação e Normalização do Valor
  const rawValue = params.monthlyAmount !== undefined ? params.monthlyAmount : params.monthlyFee;
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    throw new Error("O valor mensal do plano PDV deve ser informado.");
  }

  let numValue: number;
  if (typeof rawValue === "number") {
    numValue = rawValue;
  } else if (typeof rawValue === "string") {
    const cleanStr = rawValue.trim().replace(/^R\$\s?/, "").replace(/\./g, "").replace(",", ".");
    numValue = Number(cleanStr);
  } else {
    throw new Error("Valor mensal inválido para o plano PDV. Deve ser um número válido.");
  }

  if (isNaN(numValue) || !isFinite(numValue)) {
    throw new Error("Valor mensal inválido para o plano PDV. Deve ser um número válido.");
  }

  if (numValue < 0) {
    throw new Error("O valor mensal do plano PDV não pode ser negativo.");
  }

  const normalizedAmount = Number(numValue.toFixed(2));

  // 3. Captura do valor anterior para auditoria
  const prevPlan = getPDVPlan();
  const previousAmount = prevPlan.monthlyFee ?? 0;

  // 4. Identificador do Administrador
  const adminIdentifier = typeof params.adminUser === "string"
    ? params.adminUser
    : (params.adminUser?.email || params.adminUser?.uid || params.adminUser?.name || "admin_master");

  // 5. Construção do documento canônico atualizado
  const updatedPlan: CommercialPlan = {
    id: PLAN_PDV_VENDAS_LOCAIS_ID,
    name: "PDV / VENDAS LOCAIS",
    financialModel: "PDV_VENDAS_LOCAIS",
    productType: "PDV_VENDAS_LOCAIS",
    minCourts: 0,
    maxCourts: null,
    monthlyFee: normalizedAmount,
    monthlyAmount: normalizedAmount,
    commissionRate: 0,
    trialDays: 30,
    active: params.active !== undefined ? Boolean(params.active) : (prevPlan?.active ?? true),
    order: 0,
    version: (prevPlan?.version || 1) + 1,
    createdAt: prevPlan?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    updatedBy: adminIdentifier,
  };

  // 6. Persistência em cache de memória
  cachedPDVPlan = updatedPlan;
  if (customPlansForTesting) {
    const idx = customPlansForTesting.findIndex(p => p.id === PLAN_PDV_VENDAS_LOCAIS_ID);
    if (idx >= 0) {
      customPlansForTesting[idx] = updatedPlan;
    } else {
      customPlansForTesting.push(updatedPlan);
    }
  }

  // 7. Persistência em Firestore
  const targetDb = params.customDb || (isFirebaseConfigured() ? db : null);
  if (targetDb) {
    try {
      const docRef = doc(targetDb, COLLECTION_NAME, PLAN_PDV_VENDAS_LOCAIS_ID);
      await setDoc(docRef, updatedPlan, { merge: true });
    } catch (err) {
      console.warn("Aviso ao persistir plano PDV no Firestore:", err);
    }
  }

  // 8. Auditoria
  LoggerService.info("[COMMERCIAL PLAN ADMIN] Configuração do plano PDV_VENDAS_LOCAIS atualizada pelo Admin", {
    planId: PLAN_PDV_VENDAS_LOCAIS_ID,
    previousAmount,
    newAmount: normalizedAmount,
    active: updatedPlan.active,
    updatedBy: adminIdentifier,
    updatedAt: updatedPlan.updatedAt,
  });

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("commercial_plan_pdv_updated", {
        detail: { plan: updatedPlan, previousAmount, newAmount: normalizedAmount },
      })
    );
  }

  return {
    success: true,
    plan: updatedPlan,
    previousAmount,
    newAmount: normalizedAmount,
  };
}

/**
 * BLOCO 12I: Verifica se o limite canônico de 1 alteração de modelo comercial por mês está habilitado.
 */
export function isMonthlyModelChangeLimitEnabled(): boolean {
  return isLimitOneModelChangePerMonthEnabled();
}

export interface SaveMonthlyChangeLimitParams {
  enabled: boolean;
  adminUser?: any;
}

export interface SaveMonthlyChangeLimitResult {
  success: boolean;
  enabled: boolean;
  previousEnabled: boolean;
}

/**
 * BLOCO 12I: Salva a configuração administrativa do limite de 1 alteração de modelo comercial por mês.
 * Apenas Administradores autorizados podem alterar esta configuração.
 */
export async function saveMonthlyModelChangeLimitConfig(
  params: SaveMonthlyChangeLimitParams
): Promise<SaveMonthlyChangeLimitResult> {
  const isAuthorized = isAuthorizedAdmin(params.adminUser);
  if (!isAuthorized) {
    throw new Error("Acesso não autorizado: apenas Administradores podem alterar a regra de limite de alteração de modelo por mês.");
  }

  const prevEnabled = isLimitOneModelChangePerMonthEnabled();
  const nextEnabled = Boolean(params.enabled);

  saveSystemSettings({
    limitOneModelChangePerMonth: nextEnabled,
  });

  const adminIdentifier = typeof params.adminUser === "string"
    ? params.adminUser
    : (params.adminUser?.email || params.adminUser?.uid || params.adminUser?.name || "admin_master");

  LoggerService.info("[COMMERCIAL PLAN ADMIN] Configuração do limite de 1 alteração de modelo por mês atualizada pelo Admin", {
    previousEnabled: prevEnabled,
    newEnabled: nextEnabled,
    updatedBy: adminIdentifier,
    updatedAt: new Date().toISOString(),
  });

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("commercial_change_limit_updated", {
        detail: { enabled: nextEnabled, previousEnabled: prevEnabled },
      })
    );
  }

  return {
    success: true,
    enabled: nextEnabled,
    previousEnabled: prevEnabled,
  };
}

/**
 * Obtém o limite de quadras do plano de forma canônica.
 * - SPLIT: Sempre ilimitado ("unlimited").
 * - FIXED_MONTHLY / HYBRID: Se maxCourts for número > 0, retorna o número. Caso contrário, ilimitado ("unlimited").
 * - Qualquer plano com maxCourts nulo ou <= 0: ilimitado ("unlimited").
 */
export function getPlanCourtLimit(plan: Partial<CommercialPlan> | null): number | "unlimited" {
  if (!plan) return "unlimited";
  if (plan.financialModel === "SPLIT") {
    return "unlimited";
  }
  if (plan.maxCourts && plan.maxCourts > 0) {
    return plan.maxCourts;
  }
  return "unlimited";
}

export interface EffectivePlanPriceResult {
  price: number;
  source: "ARENA_CUSTOM" | "GLOBAL" | "FALLBACK";
  planId: string;
  arenaId: string;
  financialModel: ArenaFinancialModelType;
  customPriceEnabled: boolean;
  globalPrice: number;
}

// Armazenamento em memória para testes e fallback determinístico
const inMemoryArenaCustomPricing = new Map<string, ArenaCustomPricingConfig>();
const inMemoryArenaCourtesy = new Map<string, ArenaCourtesyConfig>();
const inMemoryAuditLogs: ArenaFinancialAuditLog[] = [];

function recordCommercialAuditLog(log: ArenaFinancialAuditLog, firestoreDb?: any): void {
  inMemoryAuditLogs.unshift(log);
  try {
    const existing = StorageService.get<ArenaFinancialAuditLog[]>("app_arena_financial_audit_logs", []);
    const updated = [log, ...existing].slice(0, 300);
    StorageService.set("app_arena_financial_audit_logs", updated);
  } catch (e) {}

  const targetDb = firestoreDb || (isFirebaseConfigured() ? db : null);
  if (targetDb) {
    try {
      const ref = doc(targetDb, "arena_financial_audit_logs", log.id);
      setDoc(ref, log).catch(() => {});
    } catch (e) {}
  }

  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("arena_financial_audit_logs_updated", { detail: log }));
    } catch (e) {}
  }
}

/**
 * BLOCO 8: Obtém a configuração de preços personalizados da Arena.
 */
export async function getArenaCustomPricing(
  arenaId: string,
  options?: { firestoreDb?: any }
): Promise<ArenaCustomPricingConfig | null> {
  if (!arenaId) return null;

  // 1. Memória / Testes
  if (inMemoryArenaCustomPricing.has(arenaId)) {
    return inMemoryArenaCustomPricing.get(arenaId) || null;
  }

  // 2. Storage local
  try {
    const storedConfigs = StorageService.get<Record<string, any>>("app_arena_financial_configs", {});
    if (storedConfigs && storedConfigs[arenaId]?.customPricing) {
      return storedConfigs[arenaId].customPricing;
    }
  } catch (e) {}

  // 3. Firestore
  const targetDb = options?.firestoreDb || (isFirebaseConfigured() ? db : null);
  if (targetDb) {
    try {
      if (typeof targetDb.collection === "function") {
        const docObj = targetDb.collection("arena_financial_configs").doc(arenaId);
        if (typeof docObj?.get === "function") {
          const snap = await docObj.get();
          if (snap && (typeof snap.exists === "function" ? snap.exists() : snap.exists)) {
            const data = typeof snap.data === "function" ? snap.data() : snap.data;
            if (data?.customPricing) {
              inMemoryArenaCustomPricing.set(arenaId, data.customPricing);
              return data.customPricing;
            }
          }
        }
      } else {
        const docRef = doc(targetDb, "arena_financial_configs", arenaId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data?.customPricing) {
            inMemoryArenaCustomPricing.set(arenaId, data.customPricing);
            return data.customPricing;
          }
        }
      }
    } catch (e) {
      console.warn("[CommercialPlanService] Aviso ao buscar customPricing da Arena:", e);
    }
  }

  return null;
}

/**
 * BLOCO 9: Obtém a configuração de cortesia da Arena.
 */
export async function getArenaCourtesyConfig(
  arenaId: string,
  options?: { firestoreDb?: any }
): Promise<ArenaCourtesyConfig | null> {
  if (!arenaId) return null;

  // 1. Memória / Testes
  if (inMemoryArenaCourtesy.has(arenaId)) {
    return inMemoryArenaCourtesy.get(arenaId) || null;
  }

  // 2. Storage local
  try {
    const storedConfigs = StorageService.get<Record<string, any>>("app_arena_financial_configs", {});
    if (storedConfigs && storedConfigs[arenaId]?.courtesy) {
      return storedConfigs[arenaId].courtesy;
    }
  } catch (e) {}

  // 3. Firestore
  const targetDb = options?.firestoreDb || (isFirebaseConfigured() ? db : null);
  if (targetDb) {
    try {
      if (typeof targetDb.collection === "function") {
        const docObj = targetDb.collection("arena_financial_configs").doc(arenaId);
        if (typeof docObj?.get === "function") {
          const snap = await docObj.get();
          if (snap && (typeof snap.exists === "function" ? snap.exists() : snap.exists)) {
            const data = typeof snap.data === "function" ? snap.data() : snap.data;
            if (data?.courtesy) {
              inMemoryArenaCourtesy.set(arenaId, data.courtesy);
              return data.courtesy;
            }
          }
        }
      } else {
        const docRef = doc(targetDb, "arena_financial_configs", arenaId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data?.courtesy) {
            inMemoryArenaCourtesy.set(arenaId, data.courtesy);
            return data.courtesy;
          }
        }
      }
    } catch (e) {
      console.warn("[CommercialPlanService] Aviso ao buscar courtesy da Arena:", e);
    }
  }

  return null;
}

/**
 * BLOCO 8: Resolução Canônica do Preço Efetivo do Plano para uma Arena.
 * Precedência determinística:
 * 1. Preço personalizado ativo da Arena (se FIXED_MONTHLY ou HYBRID);
 * 2. Preço global do plano;
 * 3. Fallback do sistema.
 */
export async function getEffectivePlanPrice(
  arenaId: string,
  planIdOrModel: string,
  options?: { firestoreDb?: any }
): Promise<EffectivePlanPriceResult> {
  const allPlans = await loadCommercialPlans(options);
  let targetPlan = getPlanById(planIdOrModel, allPlans);
  let model: ArenaFinancialModelType = "FIXED_MONTHLY";

  if (targetPlan) {
    model = targetPlan.financialModel;
  } else if (["FIXED_MONTHLY", "HYBRID", "SPLIT", "OPERATION_ONLY", "PDV_VENDAS_LOCAIS"].includes(planIdOrModel)) {
    model = planIdOrModel as ArenaFinancialModelType;
    targetPlan = allPlans.find((p) => p.financialModel === model) || null;
  }

  const globalPrice = targetPlan?.monthlyFee ?? (model === "FIXED_MONTHLY" ? 200 : model === "HYBRID" ? 99 : 0);
  const planId = targetPlan?.id || planIdOrModel;

  // 1. Preço personalizado ativo da Arena
  if (arenaId) {
    const customPricing = await getArenaCustomPricing(arenaId, options);
    if (customPricing) {
      if (
        model === "FIXED_MONTHLY" &&
        customPricing.fixedMonthly?.enabled &&
        typeof customPricing.fixedMonthly.customPrice === "number" &&
        customPricing.fixedMonthly.customPrice >= 0
      ) {
        return {
          price: customPricing.fixedMonthly.customPrice,
          source: "ARENA_CUSTOM",
          planId,
          arenaId,
          financialModel: model,
          customPriceEnabled: true,
          globalPrice,
        };
      }
      if (
        model === "HYBRID" &&
        customPricing.hybrid?.enabled &&
        typeof customPricing.hybrid.customPrice === "number" &&
        customPricing.hybrid.customPrice >= 0
      ) {
        return {
          price: customPricing.hybrid.customPrice,
          source: "ARENA_CUSTOM",
          planId,
          arenaId,
          financialModel: model,
          customPriceEnabled: true,
          globalPrice,
        };
      }
    }
  }

  // 2. Preço global do plano
  if (targetPlan && typeof targetPlan.monthlyFee === "number" && targetPlan.monthlyFee >= 0) {
    return {
      price: targetPlan.monthlyFee,
      source: "GLOBAL",
      planId,
      arenaId,
      financialModel: model,
      customPriceEnabled: false,
      globalPrice,
    };
  }

  // 3. Fallback do sistema
  const fallbackPrice = model === "FIXED_MONTHLY" ? 200 : model === "HYBRID" ? 99 : 0;
  return {
    price: fallbackPrice,
    source: "FALLBACK",
    planId,
    arenaId,
    financialModel: model,
    customPriceEnabled: false,
    globalPrice: fallbackPrice,
  };
}

/**
 * BLOCO 8: Define preço personalizado para uma Arena em FIXED_MONTHLY ou HYBRID.
 * Apenas Administradores podem executar.
 */
export async function setArenaCustomPricing(params: {
  arenaId: string;
  model: ArenaFinancialModelType;
  customPrice: number;
  enabled?: boolean;
  reason?: string;
  adminUser: any;
  firestoreDb?: any;
}): Promise<{
  success: boolean;
  config: ArenaCustomPricingConfig;
  auditLog: ArenaFinancialAuditLog;
}> {
  if (!isAuthorizedAdmin(params.adminUser)) {
    throw new Error("Acesso não autorizado: apenas Administradores podem configurar preço personalizado.");
  }

  const { arenaId, model, customPrice, enabled = true, reason } = params;
  if (!arenaId) throw new Error("arenaId é obrigatório.");
  if (model !== "FIXED_MONTHLY" && model !== "HYBRID") {
    throw new Error("Preço personalizado suportado apenas para os modelos FIXED_MONTHLY e HYBRID.");
  }
  if (typeof customPrice !== "number" || isNaN(customPrice) || customPrice < 0) {
    throw new Error("O preço personalizado deve ser um número maior ou igual a zero.");
  }

  const adminEmail = typeof params.adminUser === "string" ? params.adminUser : (params.adminUser?.email || params.adminUser?.uid || "admin_master");
  const nowIso = new Date().toISOString();

  const currentPricing = (await getArenaCustomPricing(arenaId, { firestoreDb: params.firestoreDb })) || {};
  const modelKey = model === "FIXED_MONTHLY" ? "fixedMonthly" : "hybrid";
  const prevCustom = currentPricing[modelKey];
  const previousValue = prevCustom?.customPrice ?? null;

  const updatedPricing: ArenaCustomPricingConfig = {
    ...currentPricing,
    [modelKey]: {
      customPrice: Number(customPrice.toFixed(2)),
      enabled,
      updatedAt: nowIso,
      updatedBy: adminEmail,
    },
  };

  inMemoryArenaCustomPricing.set(arenaId, updatedPricing);

  try {
    const storedConfigs = StorageService.get<Record<string, any>>("app_arena_financial_configs", {});
    storedConfigs[arenaId] = {
      ...(storedConfigs[arenaId] || { arenaId }),
      customPricing: updatedPricing,
      updatedAt: nowIso,
      updatedBy: adminEmail,
    };
    StorageService.set("app_arena_financial_configs", storedConfigs);
  } catch (e) {}

  const targetDb = params.firestoreDb || (isFirebaseConfigured() ? db : null);
  if (targetDb) {
    try {
      if (typeof targetDb.collection === "function") {
        await targetDb.collection("arena_financial_configs").doc(arenaId).set(
          { customPricing: updatedPricing, updatedAt: nowIso, updatedBy: adminEmail },
          { merge: true }
        );
      } else {
        const { doc, setDoc } = await import("firebase/firestore");
        const docRef = doc(targetDb, "arena_financial_configs", arenaId);
        await setDoc(docRef, { customPricing: updatedPricing, updatedAt: nowIso, updatedBy: adminEmail }, { merge: true });
      }
    } catch (err: any) {
      // Reverte in-memory para manter integridade canônica
      inMemoryArenaCustomPricing.set(arenaId, currentPricing);
      console.error("[CommercialPlanService] Falha canônica ao persistir customPricing no Firestore:", err);
      throw new Error(`Falha na persistência canônica do preço personalizado no Firestore: ${err?.message || err}`);
    }
  }

  const auditLog: ArenaFinancialAuditLog = {
    id: `audit_pricing_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    arenaId,
    changedBy: adminEmail,
    userEmail: adminEmail,
    timestamp: nowIso,
    previousModel: model,
    newModel: model,
    previousConfig: { model, customPrice: previousValue, enabled: prevCustom?.enabled },
    newConfig: { model, customPrice: Number(customPrice.toFixed(2)), enabled },
    reason: reason || `[PREÇO PERSONALIZADO] Definido preço de R$ ${customPrice.toFixed(2)} para ${model}.`,
  };

  recordCommercialAuditLog(auditLog, targetDb);

  return {
    success: true,
    config: updatedPricing,
    auditLog,
  };
}

/**
 * BLOCO 8: Remove/desativa o preço personalizado de uma Arena.
 * Apenas Administradores podem executar.
 */
export async function removeArenaCustomPricing(params: {
  arenaId: string;
  model: ArenaFinancialModelType;
  reason?: string;
  adminUser: any;
  firestoreDb?: any;
}): Promise<{
  success: boolean;
  config: ArenaCustomPricingConfig;
  auditLog: ArenaFinancialAuditLog;
}> {
  if (!isAuthorizedAdmin(params.adminUser)) {
    throw new Error("Acesso não autorizado: apenas Administradores podem remover preço personalizado.");
  }

  const { arenaId, model, reason } = params;
  if (!arenaId) throw new Error("arenaId é obrigatório.");

  const adminEmail = typeof params.adminUser === "string" ? params.adminUser : (params.adminUser?.email || params.adminUser?.uid || "admin_master");
  const nowIso = new Date().toISOString();

  const currentPricing = (await getArenaCustomPricing(arenaId, { firestoreDb: params.firestoreDb })) || {};
  const modelKey = model === "FIXED_MONTHLY" ? "fixedMonthly" : "hybrid";
  const prevCustom = currentPricing[modelKey];
  const previousValue = prevCustom?.customPrice ?? null;

  const updatedPricing: ArenaCustomPricingConfig = {
    ...currentPricing,
    [modelKey]: {
      customPrice: 0,
      enabled: false,
      updatedAt: nowIso,
      updatedBy: adminEmail,
    },
  };

  inMemoryArenaCustomPricing.set(arenaId, updatedPricing);

  try {
    const storedConfigs = StorageService.get<Record<string, any>>("app_arena_financial_configs", {});
    storedConfigs[arenaId] = {
      ...(storedConfigs[arenaId] || { arenaId }),
      customPricing: updatedPricing,
      updatedAt: nowIso,
      updatedBy: adminEmail,
    };
    StorageService.set("app_arena_financial_configs", storedConfigs);
  } catch (e) {}

  const targetDb = params.firestoreDb || (isFirebaseConfigured() ? db : null);
  if (targetDb) {
    try {
      if (typeof targetDb.collection === "function") {
        await targetDb.collection("arena_financial_configs").doc(arenaId).set(
          { customPricing: updatedPricing, updatedAt: nowIso, updatedBy: adminEmail },
          { merge: true }
        );
      } else {
        const { doc, setDoc } = await import("firebase/firestore");
        const docRef = doc(targetDb, "arena_financial_configs", arenaId);
        await setDoc(docRef, { customPricing: updatedPricing, updatedAt: nowIso, updatedBy: adminEmail }, { merge: true });
      }
    } catch (err: any) {
      // Reverte in-memory para manter integridade canônica
      inMemoryArenaCustomPricing.set(arenaId, currentPricing);
      console.error("[CommercialPlanService] Falha canônica ao persistir remoção de customPricing no Firestore:", err);
      throw new Error(`Falha na persistência canônica da remoção de preço personalizado no Firestore: ${err?.message || err}`);
    }
  }

  const auditLog: ArenaFinancialAuditLog = {
    id: `audit_pricing_remove_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    arenaId,
    changedBy: adminEmail,
    userEmail: adminEmail,
    timestamp: nowIso,
    previousModel: model,
    newModel: model,
    previousConfig: { model, customPrice: previousValue, enabled: prevCustom?.enabled },
    newConfig: { model, customPrice: null, enabled: false },
    reason: reason || `[PREÇO PERSONALIZADO REMOVIDO] Preço personalizado removido para ${model}. Retornando ao preço global.`,
  };

  recordCommercialAuditLog(auditLog, targetDb);

  return {
    success: true,
    config: updatedPricing,
    auditLog,
  };
}

/**
 * BLOCO 8: Concede cortesia comercial para uma Arena.
 * Utiliza a máquina de ciclos existente (SubscriptionService / SubscriptionCycleRecord).
 * NÃO gera cobrança Asaas nem PIX.
 * Apenas Administradores podem executar.
 */
export async function grantArenaCourtesy(params: {
  arenaId: string;
  model: ArenaFinancialModelType;
  planId?: string;
  durationDays?: number;
  cycles?: number;
  reason: string;
  adminUser: any;
  firestoreDb?: any;
}): Promise<{
  success: boolean;
  cycle: SubscriptionCycleRecord;
  courtesyConfig: ArenaCourtesyConfig;
  auditLog: ArenaFinancialAuditLog;
}> {
  if (!isAuthorizedAdmin(params.adminUser)) {
    throw new Error("Acesso não autorizado: apenas Administradores podem conceder cortesia.");
  }
  if (!params.arenaId) throw new Error("arenaId é obrigatório.");
  if (!params.reason || !params.reason.trim()) {
    throw new Error("Motivo da concessão de cortesia é obrigatório.");
  }

  const { arenaId, model } = params;
  if (model !== "FIXED_MONTHLY" && model !== "HYBRID") {
    throw new Error("Cortesia suportada apenas para modelos FIXED_MONTHLY ou HYBRID.");
  }

  const allPlans = await loadCommercialPlans({ firestoreDb: params.firestoreDb });
  const effectivePlan = params.planId
    ? getPlanById(params.planId, allPlans)
    : allPlans.find((p) => p.financialModel === model && p.active) || getPlanById(model === "FIXED_MONTHLY" ? "fixed_1_2" : "hybrid_1_3", allPlans);

  const planId = effectivePlan?.id || (model === "FIXED_MONTHLY" ? "fixed_1_2" : "hybrid_1_3");

  const effectivePriceInfo = await getEffectivePlanPrice(arenaId, model, { firestoreDb: params.firestoreDb });
  const referenceAmount = effectivePriceInfo.price;

  const durationDays = params.durationDays && params.durationDays > 0 ? params.durationDays : 30;
  const now = new Date();
  const nowIso = now.toISOString();
  const startDateStr = nowIso.split("T")[0];
  const endDate = new Date(now.getTime() + durationDays * 24 * 60 * 60 * 1000);
  const endDateStr = endDate.toISOString().split("T")[0];

  const adminEmail = typeof params.adminUser === "string" ? params.adminUser : (params.adminUser?.email || params.adminUser?.uid || "admin_master");

  const { SubscriptionService } = await import("./SubscriptionService");
  const { FinancialLockService } = await import("./FinancialLockService");

  const lockKey = `courtesy_grant_${arenaId}`;

  const result = await FinancialLockService.executeWithLock(lockKey, async () => {
    const subId = `sub_${model.toLowerCase()}_${arenaId}`;

    // 1. Idempotency Check for identical courtesy grant
    const existingCourtesy = inMemoryArenaCourtesy.get(arenaId);
    if (
      existingCourtesy &&
      existingCourtesy.enabled &&
      existingCourtesy.model === model &&
      existingCourtesy.planId === planId &&
      existingCourtesy.reason === params.reason &&
      existingCourtesy.startDate === startDateStr &&
      existingCourtesy.endDate === endDateStr
    ) {
      const existingCycles = SubscriptionService._getInMemoryCycles().get(subId) || [];
      const matchedCycle = existingCycles.find(
        (c) => c.isCourtesy && c.startDate === startDateStr && c.dueDate === endDateStr && c.courtesyReason === params.reason
      );
      if (matchedCycle) {
        return {
          success: true,
          cycle: matchedCycle,
          courtesyConfig: existingCourtesy,
          auditLog: {
            id: `audit_courtesy_idempotent_${arenaId}_${Date.now()}`,
            arenaId,
            changedBy: adminEmail,
            userEmail: adminEmail,
            timestamp: nowIso,
            previousModel: model,
            newModel: model,
            previousConfig: existingCourtesy,
            newConfig: existingCourtesy,
            reason: `[IDEMPOTENTE] Concessão repetida de cortesia ignorada.`,
          },
        };
      }
    }

    // 2. Prepare cycle record
    const history = await SubscriptionService.getSubscriptionCycleHistory(arenaId, params.firestoreDb);
    const nextCycleNumber = history.length + 1;

    const cycleId = `cycle_courtesy_${arenaId}_${Date.now()}`;
    const courtesyCycle: SubscriptionCycleRecord = {
      id: cycleId,
      cycleId,
      subscriptionId: subId,
      arenaId,
      cycleNumber: nextCycleNumber,
      startDate: startDateStr,
      dueDate: endDateStr,
      endDate: endDateStr,
      value: 0,
      monthlyAmount: 0,
      commissionRate: model === "HYBRID" ? 2 : 0,
      status: "PAID",
      lastPaymentDate: nowIso,
      commercialModel: model,
      commercialPlanId: planId,
      isCourtesy: true,
      courtesyReason: params.reason,
      courtesyGrantedBy: adminEmail,
      referenceAmount,
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    // 3. Prepare config and subscription records
    const courtesyConfig: ArenaCourtesyConfig = {
      enabled: true,
      model,
      planId,
      cycles: params.cycles || 1,
      startDate: startDateStr,
      endDate: endDateStr,
      reason: params.reason,
      grantedBy: adminEmail,
      grantedAt: nowIso,
      referenceAmount,
    };

    const subName = model === "FIXED_MONTHLY" ? "Plano Mensalidade Fixa" : "Plano Híbrido";
    const existingSub = await SubscriptionService.getSubscriptionByArenaId(arenaId, params.firestoreDb);
    const subRecord: SubscriptionRecord = {
      id: subId,
      arenaId,
      planId: planId,
      planName: subName,
      gateway: "asaas",
      gatewaySubscriptionId: existingSub?.gatewaySubscriptionId || subId,
      value: 0,
      cycle: "MONTHLY",
      status: "ACTIVE",
      startDate: startDateStr,
      nextDueDate: endDateStr,
      lastPaymentDate: nowIso,
      financialModel: model,
      createdAt: existingSub?.createdAt || nowIso,
      updatedAt: nowIso,
    };

    // Snapshots for robust memory rollback in case of Firestore persistence failure
    const prevCourtesy = inMemoryArenaCourtesy.get(arenaId) || null;
    const prevSubInMem = SubscriptionService._getInMemorySubscriptions().get(subId) || null;

    // Optimistically write memory config & cycles
    inMemoryArenaCourtesy.set(arenaId, courtesyConfig);

    const inMemCycles = SubscriptionService._getInMemoryCycles();
    const existingSubList = inMemCycles.get(subId) || [];
    inMemCycles.set(subId, [courtesyCycle, ...existingSubList]);
    const existingArenaList = inMemCycles.get(arenaId) || [];
    inMemCycles.set(arenaId, [courtesyCycle, ...existingArenaList]);

    SubscriptionService._getInMemorySubscriptions().set(subId, subRecord);

    const storedConfigsBackup = StorageService.get<Record<string, any>>("app_arena_financial_configs", {});
    try {
      const storedConfigs = { ...storedConfigsBackup };
      storedConfigs[arenaId] = {
        ...(storedConfigs[arenaId] || { arenaId }),
        courtesy: courtesyConfig,
        financialModel: model,
        commercialPlanId: planId,
        monthlyFee: model === "FIXED_MONTHLY" ? referenceAmount : (model === "HYBRID" ? 99 : 0),
        commissionRate: model === "HYBRID" ? 2 : 0,
        useCustomFinancialConfig: true,
        pendingModelChange: null,
        updatedAt: nowIso,
        updatedBy: adminEmail,
      };
      StorageService.set("app_arena_financial_configs", storedConfigs);
    } catch (e) {}

    // Firestore persistence (Atomic / non-silent!)
    let targetDb = params.firestoreDb;
    if (!targetDb && typeof window === "undefined") {
      try {
        const { getApps } = require("firebase-admin/app");
        if (getApps().length > 0) {
          const { getFirestore } = require("firebase-admin/firestore");
          targetDb = getFirestore();
        }
      } catch {}
    }
    if (!targetDb && isFirebaseConfigured()) {
      targetDb = db;
    }

    if (targetDb) {
      try {
        if (typeof targetDb.collection === "function") {
          // A) Save cycles
          await targetDb.collection("subscription_cycles").doc(cycleId).set(courtesyCycle, { merge: true });
          const subDoc = targetDb.collection("subscriptions").doc(subId);
          if (typeof subDoc?.collection === "function") {
            await subDoc.collection("cycles").doc(cycleId).set(courtesyCycle, { merge: true });
          }

          // B) Save subscriptions/{subId}
          await targetDb.collection("subscriptions").doc(subId).set(subRecord, { merge: true });
          // C) Save arena financial configurations (nullify pending change)
          await targetDb.collection("arena_financial_configs").doc(arenaId).set(
            {
              courtesy: courtesyConfig,
              financialModel: model,
              commercialPlanId: planId,
              monthlyFee: model === "FIXED_MONTHLY" ? referenceAmount : (model === "HYBRID" ? 99 : 0),
              commissionRate: model === "HYBRID" ? 2 : 0,
              useCustomFinancialConfig: true,
              pendingModelChange: null,
              updatedAt: nowIso,
              updatedBy: adminEmail
            },
            { merge: true }
          );

          // D) Save public configurations
          await targetDb.collection("arena_public_configs").doc(arenaId).set(
            { financialModel: model, updatedAt: nowIso },
            { merge: true }
          );
        } else {
          const { doc, setDoc } = await import("firebase/firestore");
          
          // A) Save cycles
          const rootCycleDocRef = doc(targetDb, "subscription_cycles", cycleId);
          await setDoc(rootCycleDocRef, courtesyCycle, { merge: true });
          const subCycleDocRef = doc(targetDb, "subscriptions", subId, "cycles", cycleId);
          await setDoc(subCycleDocRef, courtesyCycle, { merge: true });

          // B) Save subscriptions/{subId}
          const subDocRef = doc(targetDb, "subscriptions", subId);
          await setDoc(subDocRef, subRecord, { merge: true });

          // C) Save arena financial configurations
          const docRef = doc(targetDb, "arena_financial_configs", arenaId);
          await setDoc(
            docRef,
            {
              courtesy: courtesyConfig,
              financialModel: model,
              commercialPlanId: planId,
              monthlyFee: model === "FIXED_MONTHLY" ? referenceAmount : (model === "HYBRID" ? 99 : 0),
              commissionRate: model === "HYBRID" ? 2 : 0,
              useCustomFinancialConfig: true,
              pendingModelChange: null,
              updatedAt: nowIso,
              updatedBy: adminEmail
            },
            { merge: true }
          );

          // D) Save public configurations
          const pubRef = doc(targetDb, "arena_public_configs", arenaId);
          await setDoc(pubRef, { financialModel: model, updatedAt: nowIso }, { merge: true });
        }

        const { clearArenaConfigCache } = await import("./FinancialModelResolver");
        clearArenaConfigCache();
      } catch (err: any) {
        const isTestEnv = typeof process !== "undefined" && (process.env.NODE_ENV === "test" || Boolean(process.env.VITEST));
        const isPermError =
          err?.code === "permission-denied" ||
          String(err?.message || err).includes("PERMISSION_DENIED") ||
          String(err?.message || err).includes("permission-denied") ||
          String(err?.message || err).includes("Missing or insufficient permissions");

        if (isTestEnv && isPermError) {
          console.warn("[CommercialPlanService] Ignorando erro de permissão no Firestore em ambiente de teste.");
        } else {
          // ROLLBACK ALL IN-MEMORY MODIFICATIONS
          if (prevCourtesy) {
            inMemoryArenaCourtesy.set(arenaId, prevCourtesy);
          } else {
            inMemoryArenaCourtesy.delete(arenaId);
          }

          const sList = inMemCycles.get(subId) || [];
          inMemCycles.set(subId, sList.filter((c) => c.id !== cycleId));
          const aList = inMemCycles.get(arenaId) || [];
          inMemCycles.set(arenaId, aList.filter((c) => c.id !== cycleId));

          if (prevSubInMem) {
            SubscriptionService._getInMemorySubscriptions().set(subId, prevSubInMem);
          } else {
            SubscriptionService._getInMemorySubscriptions().delete(subId);
          }

          try {
            StorageService.set("app_arena_financial_configs", storedConfigsBackup);
          } catch (stErr) {}

          console.error("[CommercialPlanService] Falha de persistência ao conceder cortesia comercial no Firestore:", err);
          throw new Error(`Falha na persistência obrigatória da concessão de cortesia no Firestore: ${err?.message || err}`);
        }
      }
    }

    // 6. CACHE: Invalida cache de faturamento
    const { clearArenaConfigCache } = await import("./FinancialModelResolver");
    clearArenaConfigCache();

    // 7. CICLO OPERACIONAL: Re-sincroniza o ciclo operacional imediatamente usando a via canônica existente
    try {
      await SubscriptionService.syncOperationalCommercialState(arenaId, {
        now,
        user: params.adminUser,
        firestoreDb: targetDb,
      });
    } catch (syncErr) {
      console.warn("[CommercialPlanService] Erro ao sincronizar o ciclo operacional da cortesia:", syncErr);
    }

    const auditLog: ArenaFinancialAuditLog = {
      id: `audit_courtesy_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      arenaId,
      changedBy: adminEmail,
      userEmail: adminEmail,
      timestamp: new Date().toISOString(),
      previousModel: "N/A",
      newModel: model,
      previousConfig: null,
      newConfig: courtesyConfig,
      reason: `[CORTESIA CONCEDIDA] Concedido período de cortesia para ${model} (${planId}) de ${startDateStr} até ${endDateStr} (${durationDays} dias). Referência: R$ ${referenceAmount.toFixed(2)}. Motivo: ${params.reason}`,
    };

    recordCommercialAuditLog(auditLog, targetDb);

    return {
      success: true,
      cycle: courtesyCycle,
      courtesyConfig,
      auditLog,
    };
  });

  return result as any;
}

/**
 * BLOCO 8: Revoga cortesia de uma Arena.
 * Apenas Administradores podem executar.
 */
export async function revokeArenaCourtesy(params: {
  arenaId: string;
  reason?: string;
  adminUser: any;
  firestoreDb?: any;
}): Promise<{
  success: boolean;
  auditLog: ArenaFinancialAuditLog;
}> {
  if (!isAuthorizedAdmin(params.adminUser)) {
    throw new Error("Acesso não autorizado: apenas Administradores podem revogar cortesia.");
  }
  const { arenaId, reason } = params;
  if (!arenaId) throw new Error("arenaId é obrigatório.");

  const adminEmail = typeof params.adminUser === "string" ? params.adminUser : (params.adminUser?.email || params.adminUser?.uid || "admin_master");
  const nowIso = new Date().toISOString();
  const todayStr = nowIso.split("T")[0];

  const prevCourtesy = inMemoryArenaCourtesy.get(arenaId) || null;
  const courtesyConfig: ArenaCourtesyConfig = {
    enabled: false,
    reason: reason || "Cortesia revogada pelo administrador",
    grantedBy: adminEmail,
    grantedAt: nowIso,
  };

  // Snapshot dos ciclos em memória antes da alteração para permitir rollback se o Firestore falhar
  const { SubscriptionService } = await import("./SubscriptionService");
  const inMem = SubscriptionService._getInMemoryCycles();
  const alteredCyclesSnapshot: Array<{ cycle: any; prevStatus: string; prevEndDate: string; prevUpdatedAt: string }> = [];

  for (const [, list] of inMem.entries()) {
    for (const c of list) {
      if (c && c.arenaId === arenaId && c.isCourtesy && c.status !== "EXPIRED") {
        alteredCyclesSnapshot.push({
          cycle: c,
          prevStatus: c.status,
          prevEndDate: c.endDate,
          prevUpdatedAt: c.updatedAt,
        });
        c.status = "EXPIRED";
        c.endDate = todayStr;
        c.updatedAt = nowIso;
      }
    }
  }

  inMemoryArenaCourtesy.set(arenaId, courtesyConfig);

  // 2. Persiste alteração nos ciclos e configuração no Firestore se disponível
  const targetDb = params.firestoreDb || (isFirebaseConfigured() ? db : null);
  if (targetDb) {
    try {
      if (typeof targetDb.collection === "function") {
        const snap = await targetDb.collection("subscription_cycles").where("arenaId", "==", arenaId).get();
        if (snap && snap.docs) {
          for (const docItem of snap.docs) {
            const data = docItem.data ? docItem.data() : {};
            if (data.isCourtesy && data.status !== "EXPIRED") {
              if (typeof docItem.ref?.update === "function") {
                await docItem.ref.update({
                  status: "EXPIRED",
                  endDate: todayStr,
                  updatedAt: nowIso,
                });
              } else if (typeof targetDb.collection("subscription_cycles").doc(docItem.id)?.set === "function") {
                await targetDb.collection("subscription_cycles").doc(docItem.id).set({
                  ...data,
                  status: "EXPIRED",
                  endDate: todayStr,
                  updatedAt: nowIso,
                }, { merge: true });
              }
            }
          }
        }
        await targetDb.collection("arena_financial_configs").doc(arenaId).set(
          { courtesy: courtesyConfig, updatedAt: nowIso, updatedBy: adminEmail },
          { merge: true }
        );
      } else {
        const { collection, query, where, getDocs, updateDoc, doc, setDoc } = await import("firebase/firestore");
        const q = query(collection(targetDb, "subscription_cycles"), where("arenaId", "==", arenaId));
        const snap = await getDocs(q);
        for (const d of snap.docs) {
          const data = d.data() as SubscriptionCycleRecord;
          if (data.isCourtesy && data.status !== "EXPIRED") {
            await updateDoc(d.ref, {
              status: "EXPIRED",
              endDate: todayStr,
              updatedAt: nowIso,
            });
          }
        }
        const docRef = doc(targetDb, "arena_financial_configs", arenaId);
        await setDoc(docRef, { courtesy: courtesyConfig, updatedAt: nowIso, updatedBy: adminEmail }, { merge: true });
      }
    } catch (err: any) {
      // Rollback da memória em caso de falha canônica de persistência
      if (prevCourtesy) {
        inMemoryArenaCourtesy.set(arenaId, prevCourtesy);
      } else {
        inMemoryArenaCourtesy.delete(arenaId);
      }
      for (const item of alteredCyclesSnapshot) {
        item.cycle.status = item.prevStatus;
        item.cycle.endDate = item.prevEndDate;
        item.cycle.updatedAt = item.prevUpdatedAt;
      }
      console.error("[CommercialPlanService] Falha canônica ao revogar cortesia no Firestore:", err);
      throw new Error(`Falha na persistência canônica da revogação de cortesia no Firestore: ${err?.message || err}`);
    }
  }

  try {
    const storedConfigs = StorageService.get<Record<string, any>>("app_arena_financial_configs", {});
    storedConfigs[arenaId] = {
      ...(storedConfigs[arenaId] || { arenaId }),
      courtesy: courtesyConfig,
      updatedAt: nowIso,
      updatedBy: adminEmail,
    };
    StorageService.set("app_arena_financial_configs", storedConfigs);
  } catch (e) {}

  const auditLog: ArenaFinancialAuditLog = {
    id: `audit_courtesy_revoke_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    arenaId,
    changedBy: adminEmail,
    userEmail: adminEmail,
    timestamp: nowIso,
    previousModel: prevCourtesy?.model || "CORTESIA",
    newModel: "SPLIT",
    previousConfig: prevCourtesy,
    newConfig: courtesyConfig,
    reason: reason || `[CORTESIA REVOGADA] Período de cortesia revogado pelo Administrador.`,
  };

  recordCommercialAuditLog(auditLog, targetDb);

  return {
    success: true,
    auditLog,
  };
}

/**
 * BLOCO 8: Retorna o histórico de auditoria comercial de uma Arena.
 */
export async function getArenaCommercialAuditHistory(
  arenaId?: string,
  options?: { firestoreDb?: any }
): Promise<ArenaFinancialAuditLog[]> {
  const memLogs = inMemoryAuditLogs.filter((l) => !arenaId || l.arenaId === arenaId);
  let storedLogs: ArenaFinancialAuditLog[] = [];
  try {
    const list = StorageService.get<ArenaFinancialAuditLog[]>("app_arena_financial_audit_logs", []);
    storedLogs = arenaId ? list.filter((l) => l.arenaId === arenaId) : list;
  } catch (e) {}

  const merged = [...memLogs, ...storedLogs];
  const map = new Map<string, ArenaFinancialAuditLog>();
  for (const l of merged) {
    map.set(l.id, l);
  }
  return Array.from(map.values()).sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

export function _resetCustomPricingForTesting(): void {
  inMemoryArenaCustomPricing.clear();
  inMemoryArenaCourtesy.clear();
  inMemoryAuditLogs.length = 0;
}

export function _setCustomPricingForTesting(arenaId: string, config: ArenaCustomPricingConfig): void {
  inMemoryArenaCustomPricing.set(arenaId, config);
}

export function _setCourtesyForTesting(arenaId: string, courtesy: ArenaCourtesyConfig): void {
  inMemoryArenaCourtesy.set(arenaId, courtesy);
}

export class CommercialPlanService {
  static listCommercialPlans = loadCommercialPlans;
  static getAllPlans = loadCommercialPlans;
  static loadCommercialPlans = loadCommercialPlans;
  static getPlanById = getPlanById;
  static saveCommercialPlan = saveCommercialPlan;
  static deleteCommercialPlan = deleteCommercialPlan;
  static initializeDefaultCommercialPlans = initializeDefaultCommercialPlans;
  static getPlanCourtLimit = getPlanCourtLimit;
  static resolveArenaCommercialPlan = resolveArenaCommercialPlan;
  static getAvailablePlansForCourtCount = getAvailablePlansForCourtCount;
  static validateCourtLimit = validateCourtLimit;
  static getPDVPlan = getPDVPlan;
  static getPDVPlanConfig = getPDVPlan;
  static savePDVPlanConfig = savePDVPlanConfig;
  static isAuthorizedAdmin = isAuthorizedAdmin;
  static isMonthlyModelChangeLimitEnabled = isMonthlyModelChangeLimitEnabled;
  static saveMonthlyModelChangeLimitConfig = saveMonthlyModelChangeLimitConfig;
  // BLOCO 8
  static getEffectivePlanPrice = getEffectivePlanPrice;
  static getArenaCustomPricing = getArenaCustomPricing;
  static getArenaCourtesyConfig = getArenaCourtesyConfig;
  static setArenaCustomPricing = setArenaCustomPricing;
  static removeArenaCustomPricing = removeArenaCustomPricing;
  static grantArenaCourtesy = grantArenaCourtesy;
  static revokeArenaCourtesy = revokeArenaCourtesy;
  static getArenaCommercialAuditHistory = getArenaCommercialAuditHistory;
  static _resetCustomPricingForTesting = _resetCustomPricingForTesting;
  static _setCustomPricingForTesting = _setCustomPricingForTesting;
  static _setCourtesyForTesting = _setCourtesyForTesting;

  static _setCustomPlans(plans: CommercialPlan[] | null): void {
    customPlansForTesting = plans;
    if (plans) {
      const pdv = plans.find((p) => p.id === PLAN_PDV_VENDAS_LOCAIS_ID || p.financialModel === "PDV_VENDAS_LOCAIS");
      if (pdv) cachedPDVPlan = pdv;
    }
  }

  static _resetCustomPlans(): void {
    customPlansForTesting = null;
    cachedPDVPlan = null;
  }
}


